import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardContent } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { PermissionGuard } from '@components/PermissionGuard';
import { acquirerService } from '@services/acquirerService';
import companyService from '@services/companyService';
import { financialService } from '@services/financialService';
import { Acquirer, AcquirerCreditCardConfiguration, AcquirerCreditCardConfigurationFormData } from '@/types/acquirer';
import { Company } from '@/types/company';
import { FinancialAccount, formatAccountBankInfo } from '@/types/financial';

/**
 * Rótulo da conta no select.
 *
 * A descrição cadastrada é "Conta Corrente" nas duas contas da empresa, então
 * ela sozinha não distingue nada — quem escolhe precisa do banco, da agência e
 * do número. Cada parte só entra se existir: conta sem agência cadastrada não
 * deve exibir "Ag null".
 */
const accountLabel = (a: FinancialAccount): string => {
  const banco = a.bankName || (a.bankNumber ? `Banco ${a.bankNumber}` : '');
  const dados = formatAccountBankInfo(a);
  return [banco, dados, a.accountsDescription].filter(Boolean).join(' · ');
};

const emptyForm: AcquirerCreditCardConfigurationFormData = {
  acquirer_id: 0,
  company_id: 0,
  ec: 0,
  client_id: '',
  client_key: '',
  api_query_url: '',
  api_request_url: '',
  id_accounts_main: 0,
};

export const AcquirerCreditCardConfigTab: React.FC = () => {
  const [items, setItems] = useState<AcquirerCreditCardConfiguration[]>([]);
  const [acquirers, setAcquirers] = useState<Acquirer[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  // Contas de TODAS as empresas, agrupadas. A listagem precisa rotular contas de
  // empresas diferentes na mesma tela, então carregar só as da empresa em edição
  // não bastaria — e são poucas empresas para justificar buscar sob demanda.
  const [accountsByCompany, setAccountsByCompany] = useState<Record<number, FinancialAccount[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [editingItem, setEditingItem] = useState<AcquirerCreditCardConfiguration | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<AcquirerCreditCardConfigurationFormData>({ ...emptyForm });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const pageSize = 20;

  const loadLookups = async () => {
    try {
      const [acqs, comps] = await Promise.all([
        acquirerService.listAllAcquirers(),
        companyService.getCompanies(),
      ]);
      setAcquirers(acqs);
      setCompanies(comps);

      // Contas de cada empresa. Falhar aqui não impede o cadastro: a conta é
      // opcional, e o resto da tela não depende dela.
      const porEmpresa: Record<number, FinancialAccount[]> = {};
      await Promise.all(
        comps.map(async c => {
          try {
            porEmpresa[c.company_id] = await financialService.getAccounts(c.company_id);
          } catch { porEmpresa[c.company_id] = []; }
        }),
      );
      setAccountsByCompany(porEmpresa);
    } catch { /* silently ignore lookup errors */ }
  };

  /** Contas da empresa em edição, para o select. */
  const accounts = accountsByCompany[formData.company_id] ?? [];

  /** Rótulo da conta de uma configuração já salva, para a listagem. */
  const configAccountLabel = (idAccountsMain: number): string => {
    for (const lista of Object.values(accountsByCompany)) {
      const conta = lista.find(a => a.idAccountsMain === idAccountsMain);
      if (conta) return accountLabel(conta);
    }
    // Conta que não veio na listagem (excluída ou de empresa sem acesso): mostrar
    // o id é melhor que mostrar nada, porque permite investigar.
    return `Conta #${idAccountsMain}`;
  };

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await acquirerService.getCreditCardConfigs({ page: currentPage, pageSize });
      setItems(result.data);
      setTotalPages(result.pagination.totalPages);
      setTotalItems(result.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLookups(); }, []);
  useEffect(() => { loadItems(); }, [currentPage]);

  const getAcquirerName = (id: number) => acquirers.find(a => a.id === id)?.name ?? `#${id}`;
  const getCompanyLabel = (id: number) => {
    const c = companies.find(c => c.company_id === id);
    return c ? `${c.name} — ${c.document}` : `#${id}`;
  };

  const openCreateForm = () => {
    setEditingItem(null);
    setFormData({ ...emptyForm });
    setFormErrors({});
    setShowForm(true);
  };

  const openEditForm = (item: AcquirerCreditCardConfiguration) => {
    setEditingItem(item);
    setFormData({
      acquirer_id: item.acquirer_id,
      company_id: item.company_id,
      ec: item.ec,
      client_id: item.client_id ?? '',
      client_key: item.client_key ?? '',
      api_query_url: item.api_query_url ?? '',
      api_request_url: item.api_request_url ?? '',
      id_accounts_main: item.id_accounts_main ?? 0,
    });
    setFormErrors({});
    setShowForm(true);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.acquirer_id) errors.acquirer_id = 'Adquirente é obrigatório';
    if (!formData.company_id) errors.company_id = 'Empresa é obrigatória';
    if (!formData.ec) errors.ec = 'EC é obrigatório';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      setSaving(true);
      setError(null);
      if (editingItem) {
        await acquirerService.updateCreditCardConfig(editingItem.acquirer_credit_card_configuration_id, formData);
      } else {
        await acquirerService.createCreditCardConfig(formData);
      }
      setShowForm(false);
      loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await acquirerService.deleteCreditCardConfig(id);
      setDeleteConfirm(null);
      loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir');
    }
  };

  const updateField = (field: keyof AcquirerCreditCardConfigurationFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">Configurações de cartão de crédito por adquirente e empresa.</p>
        <PermissionGuard permission="receiving_hub.write">
          <Button size="sm" onClick={openCreateForm} icon={<Plus size={16} />}>Nova Configuração</Button>
        </PermissionGuard>
      </div>

      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent><p className="text-red-600 text-sm">{error}</p></CardContent>
        </Card>
      )}

      {showForm && (
        <Card className="border-emerald-200 bg-emerald-50/30">
          <CardContent>
            <h3 className="text-sm font-semibold text-slate-700 mb-3">
              {editingItem ? 'Editar Configuração Cartão' : 'Nova Configuração Cartão'}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Adquirente <span className="text-red-500">*</span></label>
                <select className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={formData.acquirer_id} onChange={e => updateField('acquirer_id', Number(e.target.value))}>
                  <option value={0}>Selecione...</option>
                  {acquirers.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                {formErrors.acquirer_id && <p className="text-xs text-red-500 mt-1">{formErrors.acquirer_id}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Empresa <span className="text-red-500">*</span></label>
                <select className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={formData.company_id} onChange={e => updateField('company_id', Number(e.target.value))}>
                  <option value={0}>Selecione...</option>
                  {companies.map(c => <option key={c.company_id} value={c.company_id}>{c.name} — {c.document}</option>)}
                </select>
                {formErrors.company_id && <p className="text-xs text-red-500 mt-1">{formErrors.company_id}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">EC <span className="text-red-500">*</span></label>
                <Input type="number" value={formData.ec || ''} onChange={e => updateField('ec', Number(e.target.value))} placeholder="Código EC" />
                {formErrors.ec && <p className="text-xs text-red-500 mt-1">{formErrors.ec}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Client ID</label>
                <Input value={formData.client_id} onChange={e => updateField('client_id', e.target.value)} placeholder="Client ID" maxLength={50} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Client Key</label>
                <Input value={formData.client_key} onChange={e => updateField('client_key', e.target.value)} placeholder="Client Key" maxLength={50} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">API Query URL</label>
                <Input value={formData.api_query_url} onChange={e => updateField('api_query_url', e.target.value)} placeholder="URL de consulta" maxLength={100} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">API Request URL</label>
                <Input value={formData.api_request_url} onChange={e => updateField('api_request_url', e.target.value)} placeholder="URL de requisição" maxLength={100} />
              </div>
              {/* A conta é o que liga a agenda de recebíveis ao extrato: a parcela
                  sabe a empresa e o EC, nunca a conta. Escolher da lista evita
                  digitar um id interno que ninguém conhece de cabeça. */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Conta de crédito</label>
                <select
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100"
                  value={formData.id_accounts_main || ''}
                  disabled={!formData.company_id}
                  onChange={e => updateField('id_accounts_main', Number(e.target.value))}
                >
                  <option value="">Não informada</option>
                  {accounts.map(a => (
                    <option key={a.idAccountsMain} value={a.idAccountsMain}>
                      {accountLabel(a)}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  {formData.company_id
                    ? 'Conta em que os créditos deste EC caem. Sem ela, a previsão de recebíveis deste estabelecimento não aparece no extrato de Conta Corrente.'
                    : 'Escolha a empresa primeiro para listar as contas.'}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={() => setShowForm(false)} icon={<X size={14} />}>Cancelar</Button>
              <Button size="sm" onClick={handleSave} loading={saving} icon={<Save size={14} />}>{editingItem ? 'Salvar' : 'Criar'}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <tr>
                <th className="text-left">ID</th>
                <th className="text-left">Adquirente</th>
                <th className="text-left">Empresa</th>
                <th className="text-left">EC</th>
                <th className="text-left">Conta de crédito</th>
                <th className="text-left">Client ID</th>
                <th className="text-right">Ações</th>
              </tr>
            </TableHeader>
            <tbody>
              {loading ? (
                [...Array(3)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(7)].map((_, j) => (
                      <TableCell key={j}><div className="h-4 bg-slate-200 rounded w-16 animate-pulse" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-slate-500 py-8">Nenhuma configuração encontrada</TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item.acquirer_credit_card_configuration_id}>
                    <TableCell className="font-mono text-sm">{item.acquirer_credit_card_configuration_id}</TableCell>
                    <TableCell>{getAcquirerName(item.acquirer_id)}</TableCell>
                    <TableCell className="text-sm">{getCompanyLabel(item.company_id)}</TableCell>
                    <TableCell className="font-mono text-sm">{item.ec}</TableCell>
                    {/* Sem conta, a previsão deste EC não aparece em extrato
                        nenhum — o aviso fica na própria linha, e não escondido
                        dentro do formulário. */}
                    <TableCell className="text-sm">
                      {item.id_accounts_main
                        ? <span className="text-slate-700">{configAccountLabel(item.id_accounts_main)}</span>
                        : <span className="text-amber-700">sem conta — não projeta no extrato</span>}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{item.client_id ?? '—'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <PermissionGuard permission="receiving_hub.write">
                          <Button variant="secondary" size="sm" onClick={() => openEditForm(item)} icon={<Edit size={14} />}>Editar</Button>
                        </PermissionGuard>
                        <PermissionGuard permission="receiving_hub.write">
                          {deleteConfirm === item.acquirer_credit_card_configuration_id ? (
                            <div className="flex items-center gap-1">
                              <Button variant="danger" size="sm" onClick={() => handleDelete(item.acquirer_credit_card_configuration_id)}>Confirmar</Button>
                              <Button variant="secondary" size="sm" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
                            </div>
                          ) : (
                            <Button variant="outline" size="sm" onClick={() => setDeleteConfirm(item.acquirer_credit_card_configuration_id)} icon={<Trash2 size={14} />}>Excluir</Button>
                          )}
                        </PermissionGuard>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">Total: {totalItems} registro(s)</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Anterior</Button>
            <span className="text-sm text-slate-600">Página {currentPage} de {totalPages}</span>
            <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Próxima</Button>
          </div>
        </div>
      )}
    </div>
  );
};
